import useResizeObserver from "@react-hook/resize-observer";
import { Box, Button, ButtonGroup, IconButton, ListItem, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import {
  cloneElement,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import {
  ZoomIn,
  ZoomOut,
  Today,
  Add,
  ArrowLeft,
  ArrowRight,
  ArrowBack,
  ArrowForward,
  ArrowBackIos,
  ArrowForwardIos,
  ChevronLeft,
  ChevronRight,
  SwipeDownAlt,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ContentCutRounded,
} from "@mui/icons-material";

import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { deDE } from "@mui/x-date-pickers/locales";
import { useLazyEffect } from "@/shared/helpers/useLazyEffect";
import ApiService from "@/api/ApiService";
import { usePersistantStore } from "@/stores/global";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { ProjectState } from "../planning/Project";
import { OneMonth, OneWeek, OneYear, ThreeDays, TwoMonth, TwoWeek } from "@/assets/icons/icons";
import HorizontalDay from "./HorizontalDay";

export const HCalendarContext = createContext<HCalendarElementsProps | null>(null);

function SelectCalendarPositionMenu({ setCalStart, calStart }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [cdate, setCdate] = useState(calStart);
  const [userDidUpdate, setUserDidUpdate] = useState(false);
  useEffect(() => setCdate(calStart), [calStart]);
  useLazyEffect(
    () => {
      if (!userDidUpdate) return;
      setCalStart(cdate);
      setAnchorEl(null);
      setUserDidUpdate(false);
    },
    [userDidUpdate, cdate],
    500,
  );
  return (
    <div>
      <LocalizationProvider
        adapterLocale="de"
        dateAdapter={AdapterDayjs}
        localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
      >
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Today />
        </IconButton>
        <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem
            onClick={() => {
              setCalStart(dayjs().startOf("isoWeek"));
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <SwipeDownAlt fontSize="small" />
            </ListItemIcon>
            <ListItemText>Heute</ListItemText>
          </MenuItem>
          <ListItem>
            <DatePicker
              slotProps={{ textField: { size: "small" } }}
              onChange={(x) => {
                setCdate(x);
                setUserDidUpdate(true);
              }}
              value={cdate}
              displayWeekNumber
            />
          </ListItem>
        </Menu>
      </LocalizationProvider>
    </div>
  );
}

export interface HCalendarElementsProps {
  calStart: dayjs.Dayjs;
  calEnd: dayjs.Dayjs;
  offset: number;
  viewStart: dayjs.Dayjs;
  viewEnd: dayjs.Dayjs;
  loadStart: dayjs.Dayjs;
  loadEnd: dayjs.Dayjs;
}

const MIN_HEIGHT = 100;

export default forwardRef(
  (
    {
      initialCalStart = dayjs().startOf("isoWeek"),
      children,
      buttons,
      projectSpan,
    }: {
      projectSpan: [string, string];
      children: JSX.Element;
      initialCalStart?: dayjs.Dayjs;
      buttons: JSX.Element;
    },
    ref,
  ) => {
    const region = usePersistantStore((store) => store.region);
    const target = useRef(null);

    const [height, setHeight] = useState(0);
    const [calStart, setCalStart] = useState(initialCalStart);

    const holidayQuery = useQuery<{ date: string }[], AxiosResponse>({
      queryKey: ["getHolidays", region],
      queryFn: ApiService.getEntitiesWithFilter("holiday", !!region ? { region: ["region", region] } : {}),
    });

    const holidays = useMemo(() => {
      if (!holidayQuery.data) return new Set([]);
      return new Set(holidayQuery.data.map((x) => x.date));
    }, [holidayQuery.data]);

    useImperativeHandle(ref, () => ({
      setCalendar: (start: dayjs.Dayjs) => setCalStart(start.startOf("isoWeek")),
    }));

    const view = useMemo(() => {
      const minVisibleWeeks = Math.floor(height / MIN_HEIGHT);

      const loadPadding = Math.ceil(minVisibleWeeks * 0.7);
      const viewPadding = minVisibleWeeks;
      const viewStart = calStart.startOf("isoWeek");
      const loadStart = viewStart.subtract(loadPadding, "week");
      const loadEnd = viewStart.add(loadPadding, "week");
      return {
        viewStart,
        viewEnd: viewStart.add(minVisibleWeeks, "week"),
        calStart: viewStart.subtract(viewPadding, "week"),
        calEnd: viewStart.add(minVisibleWeeks + viewPadding, "week"),
        loadStart,
        loadStartId: "kw" + loadStart.format("YYYY-MM-DD"),
        loadEndId: "kw" + loadEnd.format("YYYY-MM-DD"),
        loadEnd,
        offset: Math.round(Math.random() * 100),
      };
    }, [calStart, height]);

    useLayoutEffect(() => {
      if (!view) return;

      const container = target.current;
      const id = "kw" + view.viewStart.format("YYYY-MM-DD");
      const element = document.getElementById(id);

      if (container && element) {
        container.scrollTop = element.offsetTop;
      }
    }, [view]);

    const onScroll = useCallback(
      (e: React.UIEvent<HTMLElement>) => {
        const currentScroll = e.currentTarget.scrollTop;
        if (document.getElementById(view.loadStartId).offsetTop - currentScroll > 0) {
          setCalStart(view.loadStart);
        }
        if (document.getElementById(view.loadEndId).offsetTop - currentScroll <= 0) {
          setCalStart(view.loadEnd);
        }
      },
      [view, height],
    );

    useResizeObserver(target, (entry) => {
      setHeight(entry.contentRect.height);
    });

    const days = useMemo(() => {
      const days = [];

      let row = -1;
      for (let currentDate = view.calStart; currentDate < view.calEnd; currentDate = currentDate.add(1, "days")) {
        const weekday = ((parseInt(currentDate.format("d")) + 6) % 7) + 1;
        const week = currentDate.format("W");
        const dday = currentDate.format("YYYY-MM-DD");

        if (weekday === 1) {
          row++;
          days.push(
            <div
              id={"kw" + dday}
              key={"kw" + week}
              className={"hc-week weekanchor"}
              style={{
                gridRow: view.offset + row * 100 + " / " + (view.offset + (row + 1) * 100),
              }}
            >
              <div className="hc-weektext">Woche {week}</div>
            </div>,
          );
        }

        days.push(
          <HorizontalDay
            date={dday}
            key={"day" + dday}
            isOutside={(!!projectSpan[0] && dday < projectSpan[0]) || (!!projectSpan[1] && dday > projectSpan[1])}
            isHoliday={holidays.has(dday) || weekday > 5}
            style={{
              minHeight: MIN_HEIGHT + "px",
              gridRow: String(view.offset + row * 100) + " / " + String(view.offset + (row + 1) * 100),
              gridColumn: String(1 + weekday),
            }}
          />,
        );
        days.push(
          <div
            key={"dayhead" + dday}
            className={"hc-dayhead"}
            style={{
              gridRowStart: String(view.offset + row * 100),
              gridColumn: String(1 + weekday),
            }}
          >
            {currentDate.date() === 1 ? <div className="hc-dayhead-monthstart">{currentDate.format("MM/YYYY")}</div> : null}
            <div className="hc-dayhead-date">{currentDate.format("D")}</div>
          </div>,
        );
      }
      return days;
    }, [view.calStart, view.calEnd, holidays]);

    return (
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", overflow: "hidden" }}>
        <Box
          sx={{
            background: "#edf0f9",
            border: "1px solid rgba(0,0,0,0.2)",
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px",
            display: "flex",
            columnGap: "5px",
            boxShadow: "inset 0px -4px 5px rgba(0,0,0,0.1)" /*borderBottom: "none"*/,
          }}
        >
          <Box sx={{ display: "flex", flex: 1, alignItems: "center", ml: "5px" }}>{buttons}</Box>
          <Box sx={{ display: "flex", alignItems: "center", mr: "5px" }}>
            <SelectCalendarPositionMenu calStart={calStart} setCalStart={setCalStart} />
            <IconButton size="small" onClick={() => setCalStart(view.viewStart.subtract(2, "week"))}>
              <KeyboardArrowUp />
            </IconButton>
            <IconButton size="small" onClick={() => setCalStart(view.viewStart.add(2, "week"))}>
              <KeyboardArrowDown />
            </IconButton>
          </Box>
        </Box>

        {!!view ? (
          <HCalendarContext.Provider value={view}>
            <div
              ref={target}
              onScroll={onScroll}
              style={{
                userSelect: "none",
                width: "100%",
                position: "relative",
                overflow: "auto",
                "::WebkitScrollbar": { display: "none" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                display: "grid",
                flex: 1,
                gridTemplateColumns: "22px repeat(5, 3fr) repeat(2, 2fr)",
                gridTemplateRows: "auto",
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              {days}
              {children}
            </div>
          </HCalendarContext.Provider>
        ) : null}
      </Box>
    );
  },
);
